/**
 * Sportsbook Preferences Service
 * Manages sportsbook ordering preferences for the app
 */

import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from './firebase';

const STORAGE_KEY = 'bettoradvantage_sportsbook_order';

// Default sportsbooks in original order
const DEFAULT_SPORTSBOOKS = [
  { id: 'draftkings', name: 'DraftKings', description: 'Daily fantasy and sportsbook' },
  { id: 'fanduel', name: 'FanDuel', description: 'Fantasy sports and betting' },
  { id: 'betmgm', name: 'BetMGM', description: 'MGM Resorts sportsbook' },
  { id: 'betus', name: 'BetUS', description: 'Online sports betting' },
  { id: 'caesars', name: 'Caesars', description: 'Caesars Entertainment sportsbook' },
  { id: 'betrivers', name: 'BetRivers', description: 'Rush Street Interactive platform' },
  { id: 'fanatics', name: 'Fanatics', description: 'Sports merchandise and betting' },
  { id: 'mybookie', name: 'MyBookie.ag', description: 'Online sportsbook' },
  { id: 'lowvig', name: 'LowVig.ag', description: 'Reduced vig sportsbook' },
  { id: 'betonline', name: 'BetOnline.ag', description: 'Online sports betting platform' },
  { id: 'bovada', name: 'Bovada', description: 'Sports betting and casino' }
];

export const SportsbookPreferences = {
  /**
   * Get the current sportsbook order from Firestore (if authenticated) or localStorage
   * @returns {Promise<Array>} Array of sportsbook objects in user's preferred order
   */
  async getSportsbookOrder() {
    try {
      // Check if user is authenticated
      const user = auth.currentUser;
      if (user) {
        // Get from Firestore
        const userRef = doc(db, 'users', user.uid);
        const userDoc = await getDoc(userRef);
        
        if (userDoc.exists() && userDoc.data().sportsbookPreference) {
          return userDoc.data().sportsbookPreference;
        }
        
        // If no preference in Firestore, save default and return it
        await this.saveSportsbookOrder([...DEFAULT_SPORTSBOOKS]);
        return [...DEFAULT_SPORTSBOOKS];
      } else {
        // Fallback to localStorage for unauthenticated users
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
          return JSON.parse(saved);
        }
        return [...DEFAULT_SPORTSBOOKS];
      }
    } catch (error) {
      console.error('Error loading sportsbook order:', error);
      // Fallback to localStorage on error
      try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
          return JSON.parse(saved);
        }
      } catch (localStorageError) {
        console.error('Error loading from localStorage:', localStorageError);
      }
      return [...DEFAULT_SPORTSBOOKS];
    }
  },

  /**
   * Get the current sportsbook order synchronously from localStorage only
   * @returns {Array} Array of sportsbook objects in user's preferred order
   */
  getSportsbookOrderSync() {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
      return [...DEFAULT_SPORTSBOOKS];
    } catch (error) {
      console.error('Error loading sportsbook order from localStorage:', error);
      return [...DEFAULT_SPORTSBOOKS];
    }
  },

  /**
   * Save sportsbook order to Firestore (if authenticated) and localStorage
   * @param {Array} order - Array of sportsbook objects in desired order
   * @returns {Promise<boolean>} Success status
   */
  async saveSportsbookOrder(order) {
    try {
      // Always save to localStorage for immediate access
      localStorage.setItem(STORAGE_KEY, JSON.stringify(order));
      
      // Check if user is authenticated
      const user = auth.currentUser;
      if (user) {
        // Save to Firestore
        const userRef = doc(db, 'users', user.uid);
        
        try {
          // Check if document exists
          const userDoc = await getDoc(userRef);
          
          if (userDoc.exists()) {
            // Update existing document
            await updateDoc(userRef, {
              sportsbookPreference: order
            });
          } else {
            // Create new document with sportsbook preference
            await setDoc(userRef, {
              sportsbookPreference: order
            });
          }
        } catch (firestoreError) {
          console.error('Error saving to Firestore:', firestoreError);
          // Continue with localStorage save even if Firestore fails
        }
      }
      
      // Dispatch custom event to notify components of the change
      window.dispatchEvent(new CustomEvent('sportsbookOrderChanged', {
        detail: { order }
      }));
      
      return true;
    } catch (error) {
      console.error('Error saving sportsbook order:', error);
      return false;
    }
  },

  /**
   * Get ordered list of sportsbook names
   * @returns {Promise<Array>} Array of sportsbook names in user's preferred order
   */
  async getSportsbookNames() {
    const order = await this.getSportsbookOrder();
    return order.map(book => book.name);
  },

  /**
   * Get ordered list of sportsbook IDs
   * @returns {Promise<Array>} Array of sportsbook IDs in user's preferred order
   */
  async getSportsbookIds() {
    const order = await this.getSportsbookOrder();
    return order.map(book => book.id);
  },

  /**
   * Get ordered list of sportsbook names synchronously
   * @returns {Array} Array of sportsbook names in user's preferred order
   */
  getSportsbookNamesSync() {
    return this.getSportsbookOrderSync().map(book => book.name);
  },

  /**
   * Get ordered list of sportsbook IDs synchronously
   * @returns {Array} Array of sportsbook IDs in user's preferred order
   */
  getSportsbookIdsSync() {
    return this.getSportsbookOrderSync().map(book => book.id);
  },

  /**
   * Get sportsbook name by ID
   * @param {string} id - Sportsbook ID
   * @returns {Promise<string>} Sportsbook name or null if not found
   */
  async getSportsbookName(id) {
    const order = await this.getSportsbookOrder();
    const sportsbook = order.find(book => book.id === id);
    return sportsbook ? sportsbook.name : null;
  },

  /**
   * Get sportsbook name by ID synchronously
   * @param {string} id - Sportsbook ID
   * @returns {string} Sportsbook name or null if not found
   */
  getSportsbookNameSync(id) {
    const sportsbook = this.getSportsbookOrderSync().find(book => book.id === id);
    return sportsbook ? sportsbook.name : null;
  },

  /**
   * Filter and sort bookmaker data based on user preferences (async)
   * @param {Array} bookmakers - Array of bookmaker objects from odds API
   * @returns {Promise<Array>} Filtered and sorted bookmakers based on user preferences
   */
  async filterAndSortBookmakers(bookmakers) {
    if (!bookmakers || !Array.isArray(bookmakers)) {
      return [];
    }

    const orderedIds = await this.getSportsbookIds();
    const filteredBookmakers = [];

    // Add bookmakers in the user's preferred order
    orderedIds.forEach(sportsbookId => {
      const bookmaker = bookmakers.find(b => 
        b.key === sportsbookId || 
        b.title?.toLowerCase().includes(sportsbookId.toLowerCase())
      );
      if (bookmaker) {
        filteredBookmakers.push(bookmaker);
      }
    });

    // Add any remaining bookmakers that weren't in the user's list
    bookmakers.forEach(bookmaker => {
      if (!filteredBookmakers.find(b => b.key === bookmaker.key)) {
        filteredBookmakers.push(bookmaker);
      }
    });

    return filteredBookmakers;
  },

  /**
   * Filter and sort bookmaker data based on user preferences (sync, uses localStorage)
   * @param {Array} bookmakers - Array of bookmaker objects from odds API
   * @returns {Array} Filtered and sorted bookmakers based on user preferences
   */
  filterAndSortBookmakersSync(bookmakers) {
    if (!bookmakers || !Array.isArray(bookmakers)) {
      return [];
    }

    const orderedIds = this.getSportsbookIdsSync();
    const filteredBookmakers = [];

    // Add bookmakers in the user's preferred order
    orderedIds.forEach(sportsbookId => {
      const bookmaker = bookmakers.find(b => 
        b.key === sportsbookId || 
        b.title?.toLowerCase().includes(sportsbookId.toLowerCase())
      );
      if (bookmaker) {
        filteredBookmakers.push(bookmaker);
      }
    });

    // Add any remaining bookmakers that weren't in the user's list
    bookmakers.forEach(bookmaker => {
      if (!filteredBookmakers.find(b => b.key === bookmaker.key)) {
        filteredBookmakers.push(bookmaker);
      }
    });

    return filteredBookmakers;
  },

  /**
   * Reset to default sportsbook order
   * @returns {Promise<boolean>} Success status
   */
  async resetToDefaults() {
    try {
      // Save default order (this will update both Firestore and localStorage)
      await this.saveSportsbookOrder([...DEFAULT_SPORTSBOOKS]);
      return true;
    } catch (error) {
      console.error('Error resetting sportsbook preferences:', error);
      return false;
    }
  },

  /**
   * Get default sportsbooks
   * @returns {Array} Array of default sportsbook objects
   */
  getDefaultSportsbooks() {
    return [...DEFAULT_SPORTSBOOKS];
  }
};

export default SportsbookPreferences;